import { FiLoader, FiX } from "react-icons/fi"
import { MessageType } from "../../types/MessageType"
import { BananaTypeProps } from "../../types/BananaType";
import { useEffect, useRef } from "react";
import { animate, AnimationPlaybackControls } from "framer-motion";
import ConfettiCanvas, { shootFireworks } from "../../components/ConfettiCanvas";

interface Props {
  funMessageIndex: number;
  isLoading: boolean;
  isPublic: boolean;
  senderName: string;
  recipientName: string;
  message: string;
  title?: string;
  selectedBanana: BananaTypeProps | null | undefined;
  onCloseClick: (banana: BananaTypeProps | null) => void;
  setIsPublic?: (value: boolean) => void;
  setSenderName: (value: string) => void;
  setRecipientName: (value: string) => void;
  setMessage: (value: string) => void;
  sendBanana: () => void;
  hideCloseButton?: boolean;
  showWrapingAnimation?: boolean;
  suggestedMessages?: string[];
}

export const SendBananaMessageView = ({
  funMessageIndex,
  isLoading,
  isPublic,
  senderName,
  recipientName,
  message,
  title,
  selectedBanana,
  onCloseClick,
  setIsPublic,
  setSenderName,
  setRecipientName,
  setMessage,
  sendBanana,
  hideCloseButton,
  showWrapingAnimation = false,
  suggestedMessages = []
}: Props) => {

  const bananaImgRef = useRef<HTMLImageElement>(null);
  const bananaPresentImgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
  }, []);

  useEffect(() => {
    if (!showWrapingAnimation) return;

    const bananaEl = bananaImgRef.current;
    const giftEl = bananaPresentImgRef.current;
    if (!bananaEl || !giftEl) return;

    // reset state
    bananaEl.style.opacity = "1";
    bananaEl.style.transform = "rotate(0deg)";
    bananaEl.style.display = "block";

    giftEl.style.opacity = "0";
    giftEl.style.transform = "rotate(0deg)";
    giftEl.style.display = "block";

    // banana: tilt left -> spin fast -> fade out
    const bananaAnim = animate(
      bananaEl,
      {
        rotate: [0, -15, 1440],
        opacity: [1, 1, 0],
        scale: [1, 1, 1]
      },
      { duration: 2.5, ease: "easeOut", times: [0, 0.1, 1] }
    ).then(() => {
      bananaEl.style.display = "none"; // optional, hide once gone
    });

    // gift: fade in while spinning fast -> slow to stop
    const giftAnim = animate(
      giftEl,
      {
        rotate: [0, 1450, 1440, 1440, 1440],
        opacity: [0, 1, 1, 1, 1],
        scale: [1, 1, 1, 1.1, 1]
      },
      { duration: 3.0, ease: "easeOut", delay: 0.5, times: [0, 0.7, 0.8, 0.9, 1] }
    );

    const fireworksTimeout = setTimeout(() => {
      shootFireworks(400);
    }, 3200);

    // cleanup function (stop if unmounted)
    return () => {
      (bananaAnim as unknown as AnimationPlaybackControls)?.stop?.();
      (giftAnim as unknown as AnimationPlaybackControls)?.stop?.();
      clearTimeout(fireworksTimeout);
    };
  }, [showWrapingAnimation])

  return <>
    <div className="max-w-2xl mx-auto bg-white rounded-xl shadow p-6 space-y-6">
      {title && <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
        {title}
      </h2>}
      <div className="relative flex flex-col sm:flex-row items-center gap-6">
        {!hideCloseButton && <button
          onClick={() => onCloseClick(null)}
          className="absolute top-0 right-0 p-1 text-gray-400 hover:text-gray-600 transition cursor-pointer"
          aria-label="Close"
        >
          <FiX size={20} />
        </button>}

        <div className="relative w-48 h-48 overflow-visible">
          <img
            src={selectedBanana?.img}
            alt={selectedBanana?.title}
            ref={bananaImgRef}
            className="absolute inset-0 m-auto w-48 h-48 object-contain rounded-md"
          />
          <img
            src="/logos/bananas/present.png"
            alt="Wrapped banana"
            ref={bananaPresentImgRef}
            style={{ opacity: 0 }}
            className="absolute inset-0 m-auto w-48 h-48 object-contain rounded-md"
          />
          <ConfettiCanvas top={-300} left={-200} />
        </div>
        <div className="text-center sm:text-left">
          <h2 className="text-2xl font-bold text-gray-900">{selectedBanana?.title}</h2>
          <p className="text-gray-600 mt-2">{selectedBanana?.description}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Your Name (Optional)</label>
          <input
            type="text"
            maxLength={100}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-yellow-400 focus:border-yellow-400"
            value={senderName}
            onChange={(event) => setSenderName(event.target.value)}
            placeholder="Banana sender"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">To (Optional)</label>
          <input
            type="text"
            maxLength={100}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:ring-yellow-400 focus:border-yellow-400"
            value={recipientName}
            onChange={(event) => setRecipientName(event.target.value)}
            placeholder="Banana recipient, for your own reference"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Message (Optional)</label>
          {suggestedMessages.length > 0 && (
            <div className="mt-2 grid gap-2">
              {suggestedMessages.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setMessage(suggestion)}
                  className={`rounded-xl border px-3 py-2 text-left text-sm transition ${message === suggestion ? "border-yellow-400 bg-yellow-50" : "border-gray-200 bg-white hover:border-yellow-300"}`}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
          <textarea
            rows={3}
            maxLength={150}
            className="mt-3 w-full rounded-md border-gray-300 shadow-sm focus:ring-yellow-400 focus:border-yellow-400"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Write your sweet banana message..."
          />
        </div>

        {setIsPublic && <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          Legacy privacy control. New Relay bananas are always delivered privately.
        </div>}
      </div>

      <div>
        <p className="text-sm sm:text-base text-gray-700 italic border-l-4 border-yellow-400 pl-4 sm:pl-6 mb-8">
          {MessageType.SEND_BANANA_FUN_MESSAGES[funMessageIndex]}
        </p>
      </div>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={sendBanana}
          disabled={isLoading}
          className={`px-6 py-3 bg-yellow-400 text-gray-900 font-semibold rounded-md shadow transition 
              ${isLoading ? "opacity-60 cursor-not-allowed" : "hover:bg-yellow-300 cursor-pointer"}`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <FiLoader className="animate-spin" />
              🎁 Wrapping...
            </span>
          ) : (
            "🎁 Let's wrap it!"
          )}
        </button>
      </div>
    </div>
  </>
}
